// Fake component controller so this code is autoloaded
//
import './animation_component.css'
import { delay, inView } from 'motion'
import { Controller } from 'stimulus'

const animationTriggered = {}
const IN_VIEW_CLASS = 'builder-animation-node-in-view'
const COMPLETED_CLASS = 'builder-animation-node-animation-completed'

export default class extends Controller {
  #generation = 0

  initialize() {
    const nodeId = this.element.dataset.nodeId
    // Wrappers inside a node (e.g. the header CTA) have no node id of their own: skip the
    // once-per-node dedupe for them rather than sharing one `undefined` key between them.
    if (nodeId) {
      if (animationTriggered[nodeId]) return

      animationTriggered[nodeId] = true
    }

    this.#setupAnimationTrigger()
  }

  retriggerAnimation(event) {
    const { node_id, style, speed } = event.detail
    if (node_id !== this.element.dataset.nodeId) return

    this.#replaceClassStartingWith(
      'builder-animation-style-',
      style === 'none' ? null : `builder-animation-style-${style}`
    )
    this.#replaceClassStartingWith(
      'builder-animation-speed-',
      style === 'none' ? null : `builder-animation-speed-${speed}`
    )

    if (style === 'none') {
      this.element.classList.remove('builder-animation')
      // Otherwise a later re-enable's fresh initialize() sees this node_id
      // already marked triggered and silently skips replaying the entrance.
      delete animationTriggered[node_id]
      return
    }

    // Reenabling from none stripped this class (above, on a prior call); the
    // apply block only ever adds it on a full server render.
    this.element.classList.add('builder-animation')

    animationTriggered[node_id] = true
    this.#setupAnimationTrigger()
  }

  #replaceClassStartingWith(prefix, replacement) {
    Array.from(this.element.classList)
      .filter((cls) => cls.startsWith(prefix))
      .forEach((cls) => this.element.classList.remove(cls))
    if (replacement) this.element.classList.add(replacement)
  }

  #setupAnimationTrigger() {
    this.#cleanup()

    // inView/delay/setTimeout callbacks from an earlier setup can still fire
    // after an editor retrigger; the generation check keeps them from marking
    // the replayed animation in-view or completed early.
    const generation = ++this.#generation

    // If current node is a container, apply to all elements within.
    // Otherwise, apply to the current element
    const elements = this.element.classList.contains('builder-node-container')
      ? Array.from(this.element.querySelectorAll('.builder-node-element'))
      : [this.element]

    elements.forEach((el) => {
      el.classList.add('builder-animation-node-should-animate')
    })

    // See the big comment in animation_component.css
    document.body.classList.add('builder-animation-guard-node-should-animate')

    inView(
      elements,
      (el) => {
        delay(() => {
          if (generation !== this.#generation) return

          // Read from el, not this.element: an element inside a nested
          // animated row transitions at that row's speed, so the completed
          // class must wait for its own transition, not the container's.
          const transitionDurationSecs =
            parseFloat(
              getComputedStyle(el).getPropertyValue(
                '--builder-animation-transition-duration'
              )
            ) || 0.6

          el.classList.add(IN_VIEW_CLASS)
          setTimeout(() => {
            if (generation !== this.#generation) return

            // Releases the in-view transform/clip-path (see
            // animation_component.css) before listeners measure the node.
            el.classList.add(COMPLETED_CLASS)
            this.dispatch('animation-completed', { target: el })
          }, transitionDurationSecs * 1000)
        }, 0.3)
      },
      { amount: 0.05 }
    )
  }

  #cleanup() {
    // For a non-container node, #setupAnimationTrigger puts this class on
    // this.element itself (not a descendant) -- querySelectorAll alone would
    // never find it there, leaving a leaf node stuck in its completed state
    // on every later retrigger.
    this.element.classList.remove(IN_VIEW_CLASS, COMPLETED_CLASS)
    this.element
      .querySelectorAll(`.${IN_VIEW_CLASS}, .${COMPLETED_CLASS}`)
      .forEach((el) => {
        el.classList.remove(IN_VIEW_CLASS, COMPLETED_CLASS)
      })
  }
}
